'use client';

import posthog from 'posthog-js';
import { isPostHogReady } from '@/lib/posthog-client';

export function charCountBucket(text: string | undefined): 'short' | 'medium' | 'long' {
  const len = text?.length ?? 0;
  if (len < 100) return 'short';
  if (len < 500) return 'medium';
  return 'long';
}

function capture(event: string, category: string, props?: Record<string, unknown>) {
  if (typeof window === 'undefined') return;
  // PostHog only initializes once Cookiebot reports statistics consent, so
  // events raised before that are dropped rather than queued.
  if (!isPostHogReady()) return;
  posthog.capture(event, { event_category: category, ...props });
}

export const analytics = {
  // ── Application ──────────────────────────────────────────
  applicationStarted: () =>
    capture('application_started', 'application'),

  applicationApproved: () =>
    capture('application_approved', 'application'),

  applicationRejected: () =>
    capture('application_rejected', 'application'),
  
  applicationSubmitted: () =>
    capture('application_submitted', 'application'),

  // ── Checkout ────────────────────────────────────────────────────
  checkoutStarted: () =>
    capture('checkout_started', 'checkout'),

  // ── Activation ──────────────────────────────────────────────────
  accountActivated: (props: { user_id: string }) =>
    capture('account_activated', 'activation', props),

  profileCompleted: () =>
    capture('profile_completed', 'activation'),

  notificationsEnabled: (props?: { channel?: string }) =>
    capture('notifications_enabled', 'activation', props),

  // ── Engagement ──────────────────────────────────────────────────
  postCreated: (props: {
    has_source: boolean;
    source_type?: string;
    char_count_bucket: 'short' | 'medium' | 'long';
    group_count: number;
  }) => capture('post_created', 'engagement', props),

  commentCreated: (props: {
    post_id: string;
    char_count_bucket: 'short' | 'medium' | 'long';
    is_reply: boolean;
  }) => capture('comment_created', 'engagement', props),

  eventCreated: (props: {
    category_id: string;
    has_group: boolean;
    is_free: boolean;
  }) => capture('event_created', 'engagement', props),

  businessCreated: (props: { category_id: string; has_location: boolean }) =>
    capture('business_created', 'engagement', props),

  educationCreated: (props: { category_id: string }) =>
    capture('education_created', 'engagement', props),

  providerCreated: (props: { category_id: string; media_count: number }) =>
    capture('provider_created', 'engagement', props),

  isoProviderCreated: (props: { category_id: string }) =>
    capture('iso_provider_created', 'engagement', props),

  listingCreated: (props: {
    category_id: string;
    is_free: boolean;
    media_count: number;
  }) => capture('listing_created', 'engagement', props),

  // ── Growth ──────────────────────────────────────────────────────
  inviteLinkGenerated: () =>
    capture('invite_link_generated', 'growth'),

  inviteSent: (props: { count: number; channel: 'email' | 'social' | 'direct' }) =>
    capture('invite_sent', 'growth', props),

  inviteAccepted: (props: { inviter_id: string }) =>
    capture('invite_accepted', 'growth', props),

  // ── Membership ──────────────────────────────────────────────────
  autoRenewCanceled: () =>
    capture('auto_renew_canceled', 'membership'),

  autoRenewReactivated: () =>
    capture('auto_renew_reactivated', 'membership'),

  // ── Feedback ────────────────────────────────────────────────────
  feedbackSubmitted: (props?: { category?: string }) =>
    capture('feedback_submitted', 'feedback', props),

  bugReported: () =>
    capture('bug_reported', 'feedback'),
};
